import { createAction } from '@reduxjs/toolkit';
import { Wallet } from 'dragonmouse-shared/economy';
import {
	SceneChat,
	DesignSystem,
	DesignSystemVersion,
	DsChat,
	ChatRef,
	Frame,
	Scene,
	Subscription,
	User,
} from 'dragonmouse-shared/interfaces';
import { OptimisticWrite } from './reducers';

//------------------------------------------------------------------------------------------------
// scenes
//------------------------------------------------------------------------------------------------
export const updateScenes = createAction<Scene[]>('GET_SCENES');
export const optimisticallyUpdateScenes = createAction<OptimisticWrite<Scene>[]>(
	'OPTIMISTICALLY_UPDATE_SCENES'
);
export const resolveOptimisticallyUpdatedScenes = createAction<string[]>(
	'RESOLVE_OPTIMISTICALLY_UPDATED_SCENES'
);
export const setSceneLoadError = createAction<string | null>('SET_SCENE_LOAD_ERROR');
// guest
export const updateGuestScenes = createAction<Scene[]>('UPDATE_GUEST_SCENES');

//------------------------------------------------------------------------------------------------
// design systems
//------------------------------------------------------------------------------------------------
export const updateDesignSystems = createAction<DesignSystem[]>('UPDATE_DESIGN_SYSTEMS');
export const optimisticallyUpdateDesignSystems = createAction<OptimisticWrite<DesignSystem>[]>(
	'OPTIMISTICALLY_UPDATE_DESIGN_SYSTEMS'
);
export const resolveOptimisticallyUpdatedDesignSystems = createAction<string[]>(
	'RESOLVE_OPTIMISTICALLY_UPDATED_DESIGN_SYSTEMS'
);
export const setDesignSystemLoadError = createAction<string | null>('SET_DESIGN_SYSTEM_LOAD_ERROR');
export const updateActiveDesignSystemVersion = createAction<DesignSystemVersion | null>(
	'UPDATE_ACTIVE_DESIGN_SYSTEM_VERSION'
);
export const updateLatestPublishedVersionNumber = createAction<number | null>(
	'UPDATE_LATEST_PUBLISHED_VERSION_NUMBER'
);
//------------------------------------------------------------------------------------------------
// ds chat
//------------------------------------------------------------------------------------------------
export const updateDsChatRefs = createAction<ChatRef[]>('UPDATE_DS_CHAT_REFS');
export const updateActiveDsChat = createAction<DsChat | null>('UPDATE_ACTIVE_DS_CHAT');
export const optimisticallyUpdateDsChats = createAction<OptimisticWrite<DsChat>[]>(
	'OPTIMISTICALLY_UPDATE_DS_CHATS'
);
export const resolveOptimisticallyUpdatedDsChats = createAction<string[]>(
	'RESOLVE_OPTIMISTICALLY_UPDATED_DS_CHATS'
);

//------------------------------------------------------------------------------------------------
// frames
//------------------------------------------------------------------------------------------------
export const updateFrames = createAction<Frame[]>('UPDATE_FRAMES');
export const optimisticallyUpdateFrames = createAction<OptimisticWrite<Frame>[]>(
	'OPTIMISTICALLY_UPDATE_FRAMES'
);
export const resolveOptimisticallyUpdatedFrames = createAction<string[]>(
	'RESOLVE_OPTIMISTICALLY_UPDATED_FRAMES'
);
export const refreshFramePermissions = createAction('REFRESH_FRAME_PERMISSIONS');
// guest
export const updateGuestFrames = createAction<Frame[]>('UPDATE_GUEST_FRAMES');

//------------------------------------------------------------------------------------------------
// scene chats
//------------------------------------------------------------------------------------------------
export const updateActiveSceneChat = createAction<SceneChat | null>('UPDATE_ACTIVE_SCENE_CHAT');
export const optimisticallyUpdateSceneChats = createAction<OptimisticWrite<SceneChat>[]>(
	'OPTIMISTICALLY_UPDATE_SCENE_CHATS'
);
export const resolveOptimisticallyUpdatedSceneChats = createAction<string[]>(
	'RESOLVE_OPTIMISTICALLY_UPDATED_SCENE_CHATS'
);
export const updateSceneChatRefs = createAction<ChatRef[]>('UPDATE_SCENE_CHAT_REFS');
export const updateActiveSceneDesignSystem = createAction<DesignSystem | null>(
	'UPDATE_ACTIVE_SCENE_DESIGN_SYSTEM'
);
export const updateActiveSceneLinkedDesignSystemVersion = createAction<DesignSystemVersion | null>(
	'UPDATE_ACTIVE_SCENE_LINKED_DS_VERSION'
);
export const updateActiveSceneLatestDesignSystemVersion = createAction<DesignSystemVersion | null>(
	'UPDATE_ACTIVE_SCENE_LATEST_DS_VERSION'
);

//------------------------------------------------------------------------------------------------
// user
//------------------------------------------------------------------------------------------------
export const updateUser = createAction<User | null>('UPDATE_USER');
export const optimisticallyUpdateUser = createAction<OptimisticWrite<User>[]>(
	'OPTIMISTICALLY_UPDATE_USER'
);
export const resolveOptimisticallyUpdatedUser = createAction<string[]>(
	'RESOLVE_OPTIMISTICALLY_UPDATED_USER'
);

//------------------------------------------------------------------------------------------------
// subscription
//------------------------------------------------------------------------------------------------
export const updateSubscription = createAction<Subscription | null>('UPDATE_SUBSCRIPTION');

//------------------------------------------------------------------------------------------------
// wallet
//------------------------------------------------------------------------------------------------
export const updateWallet = createAction<Wallet | null>('UPDATE_WALLET');

//------------------------------------------------------------------------------------------------
// tracked users
//------------------------------------------------------------------------------------------------
export const updateTrackedUsers = createAction<User[]>('UPDATE_TRACKED_USERS');

//------------------------------------------------------------------------------------------------
// loading state (tracks things that are loaded by streams)
//------------------------------------------------------------------------------------------------
export type LoadingStateType = 'never-loaded' | 'loading' | 'loaded';

export interface LoadingState {
	profile: LoadingStateType;
	scenes: LoadingStateType;
	activeDesignSystemVersion: LoadingStateType;
	activeDsChat: LoadingStateType;
	activeScene: {
		frames: LoadingStateType;
		chats: LoadingStateType;
	};
	guest: {
		scenes: LoadingStateType;
		frames: LoadingStateType;
	};
}

export const setLoadingState = createAction<{ resource: string; state: LoadingStateType }>(
	'SET_LOADING_STATE'
);
